import { LockOutlined, PlusOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons"
import { Avatar, Button, Empty, Flex, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { NotificationBadge } from "../../../components/Shared/NotificationBadge.tsx"
import { conversationTimestamp } from "../helpers/format-timestamp.ts"
import { toDisplayText } from "../helpers/mentions.ts"
import type { Conversation } from "../types/messaging.types.ts"

interface Section {
  key: string
  label: string
  rows: Conversation[]
}

interface ProjectConversationListProps {
  teamChat: Conversation | null
  customerChat: Conversation | null
  groups: Conversation[]
  directs: Conversation[]
  activeId: string | null
  canCreate: boolean
  onSelect: (conversation: Conversation) => void
  onNewConversation: () => void
}

/**
 * The project's rooms, beside the open one.
 *
 * Grouped by what each room *is* rather than filtered by it: a project has a
 * handful of conversations, so sections read faster than a filter control and
 * cost no interaction at all.
 */
export function ProjectConversationList({
  teamChat,
  customerChat,
  groups,
  directs,
  activeId,
  canCreate,
  onSelect,
  onNewConversation
}: ProjectConversationListProps) {
  const { t } = useTranslation()

  const sections: Section[] = [
    {
      key: "internal",
      label: t("messages.sections.internal", "Internal"),
      rows: teamChat ? [teamChat] : []
    },
    {
      key: "groups",
      label: t("messages.sections.groups", "Groups"),
      rows: groups
    },
    {
      key: "customer",
      label: t("messages.sections.customer", "Customer"),
      rows: customerChat ? [customerChat] : []
    },
    {
      key: "direct",
      label: t("messages.sections.direct", "Private"),
      rows: directs
    }
  ].filter((section) => section.rows.length > 0)

  const empty = sections.length === 0

  return (
    <div className={"flex h-full min-h-0 flex-col bg-white"}>
      <Flex
        align={"center"}
        justify={"space-between"}
        className={"border-b border-slate-200 px-3 py-2"}
      >
        <Typography.Text strong className={"text-sm"}>
          {t("messages.conversations", "Conversations")}
        </Typography.Text>
        {canCreate && (
          <Tooltip title={t("messages.newConversation", "New conversation")}>
            <Button
              size={"small"}
              type={"text"}
              icon={<PlusOutlined />}
              onClick={onNewConversation}
              aria-label={t("messages.newConversation", "New conversation")}
            />
          </Tooltip>
        )}
      </Flex>

      <div className={"min-h-0 flex-1 overflow-y-auto"}>
        {empty && (
          <Empty
            className={"py-10"}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Typography.Text type={"secondary"} className={"text-xs"}>
                {t(
                  "messages.noRooms",
                  "No conversations here yet."
                )}
              </Typography.Text>
            }
          />
        )}

        {sections.map((section) => (
          <div key={section.key}>
            <Typography.Text
              type={"secondary"}
              className={"block px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide"}
            >
              {section.label}
            </Typography.Text>
            {section.rows.map((conversation) => (
              <Row
                key={conversation.id}
                conversation={conversation}
                active={conversation.id === activeId}
                onSelect={onSelect}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

const ICONS = {
  project_team: <TeamOutlined />,
  contributor_group: <TeamOutlined />,
  customer: <UserOutlined />,
  direct: <LockOutlined />
}

function Row({
  conversation,
  active,
  onSelect
}: {
  conversation: Conversation
  active: boolean
  onSelect: (conversation: Conversation) => void
}) {
  const { t } = useTranslation()
  const unread = conversation.unread_count > 0

  return (
    <button
      type={"button"}
      data-testid={"conversation-row"}
      aria-current={active}
      onClick={() => onSelect(conversation)}
      className={`flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-slate-50 ${
        active ? "bg-blue-50 shadow-[inset_3px_0_0_0_#2563EB]" : ""
      }`}
    >
      <Avatar
        size={28}
        className={"mt-0.5 shrink-0"}
        style={{ background: active ? "#2563EB" : "#94A3B8" }}
        icon={ICONS[conversation.kind]}
      />
      <div className={"min-w-0 flex-1"}>
        <Flex align={"center"} gap={6} className={"min-w-0"}>
          <Typography.Text
            ellipsis
            className={`min-w-0 flex-1 text-[13px] ${unread ? "font-semibold" : ""}`}
          >
            {conversation.title}
          </Typography.Text>
          <Typography.Text type={"secondary"} className={"shrink-0 text-[10px]"}>
            {conversationTimestamp(conversation.last_message_at)}
          </Typography.Text>
        </Flex>
        <Flex align={"center"} justify={"space-between"} gap={6}>
          <Typography.Text
            type={"secondary"}
            ellipsis
            className={`min-w-0 flex-1 text-[11px] ${unread ? "!text-slate-700" : ""}`}
          >
            {conversation.last_message
              ? conversation.last_message.is_deleted
                ? t("messages.deletedPlaceholder", "This message was deleted.")
                : `${conversation.last_message.sender_name}: ${toDisplayText(
                    conversation.last_message.body,
                    conversation.participants
                  )}`
              : t("messages.noMessagesYet", "No messages yet")}
          </Typography.Text>
          {unread && <NotificationBadge count={conversation.unread_count} />}
        </Flex>
      </div>
    </button>
  )
}
